// @ts-nocheck
import { KeyboardAltOutlined } from "@mui/icons-material";
import { USER_ROLES } from "../../../staticData/userRoles";
import { CONTRACT_STATUS } from "../../constants/nmaes";
import { WEB_CONTRACTOR_CONSENT } from "../../constants/urls";
import { isUserRole } from "../storeFunc";
import { AGREEMENT_CONTRACTOR_TYPES } from "../../constants/configs";
import dayjs from "dayjs";
import { alertDialogAciton } from "../../../store/actions/auth";

/**
 * get status color of contract
 * @param {string} status
 * @returns string
 */
export function getStatusColor(status) {
  switch (status) {
    case CONTRACT_STATUS.SENT:
      return "#007BFF";
    case CONTRACT_STATUS.REVIEW:
      return "#FFA500";
    case CONTRACT_STATUS.VERIFIED:
      return "#28A745";
    case CONTRACT_STATUS.DISCARDED:
      return "#DC3545";
  }
}

/**
 * get status text of contract
 * @param {string} status
 * @returns string
 */
export function getStatusText(status) {
  switch (status) {
    case CONTRACT_STATUS.SENT:
      if (isUserRole(USER_ROLES.ADMIN, USER_ROLES.SUPERADMIN)) {
        return "Sent";
      } else {
        return "New contract";
      }
    case CONTRACT_STATUS.REVIEW:
      if (isUserRole(USER_ROLES.ADMIN, USER_ROLES.SUPERADMIN)) {
        return "Review Required";
      } else {
        return "Under Review";
      }
    case CONTRACT_STATUS.VERIFIED:
      return "Verified";
    case CONTRACT_STATUS.DISCARDED:
      return "Discarded";
  }
}

/**
 * Gets the title for contract status based on status, contract data, and user role
 * @param {Object} params - Parameters object
 * @param {string} params.status - The contract status
 * @param {Object} params.contractData - The contract data object
 * @param {string} params.userRole - The user's role
 * @returns {string} The appropriate title for the contract status
 */
export function getTitleForContractStatus({ status, contractData, userRole }) {
  switch (status) {
    case CONTRACT_STATUS.SENT:
      if (userRole === USER_ROLES.SUPERADMIN || userRole === USER_ROLES.ADMIN) {
        return { title: "Send On", value: contractData?.sentOn };
      } else {
        return { title: "Received On", value: contractData?.sentOn };
      }
    case CONTRACT_STATUS.REVIEW:
      if (userRole === USER_ROLES.SUPERADMIN || userRole === USER_ROLES.ADMIN) {
        return { title: "Send On", value: contractData?.sentOn };
      } else {
        return { title: "Received On", value: contractData?.sentOn };
      }
    case CONTRACT_STATUS.VERIFIED:
      if (userRole === USER_ROLES.SUPERADMIN || userRole === USER_ROLES.ADMIN) {
        return { title: "Verified On", value: contractData?.verifiedOn };
      } else {
        return { title: "Verified On", value: contractData?.verifiedOn };
      }
    case CONTRACT_STATUS.DISCARDED:
      if (userRole === USER_ROLES.SUPERADMIN || userRole === USER_ROLES.ADMIN) {
        return { title: "Send On", value: contractData?.sentOn };
      } else {
        return { title: "Received On", value: contractData?.sentOn };
      }
    default:
      return { title: "Update On", value: contractData?.updatedAt };
  }
}

/**
 * generate contract object for contractor types
 * @param {Object} obj
 * @param {String} obj.contractorType - contractor type of current user
 * @param {Array<Object>} obj.allContracts - all contracts of that user
 * @returns {Object} list of contracts that can be used in state
 */
export function getContractsForState({
  contractorType,
  allContracts = [],
  contractorPDFUrls
}) {
  return contractorPDFUrls?.[contractorType]?.map((co) => ({
    ...co,
    isAccepted: false
  }));
}

/**
 * generate document for selected contractor
 */
export function getContractorAgreementState({
  existingDocument,
  contractorType,
  contractorPDFUrls
}) {
  if (!AGREEMENT_CONTRACTOR_TYPES?.includes(contractorType)) return;

  let keyValObj = {};
  let originalDocMap = {};
  let typeMap = {};

  contractorPDFUrls?.[contractorType]?.forEach((c) => {
    keyValObj[c?.matchToKey] = c?.title;
    originalDocMap[c?.matchToKey] = c?.url;
    typeMap[c?.matchToKey] = c?.matchToKey;
  });

  const contracts = contractorPDFUrls?.[contractorType];

  const result = contracts?.map((type) => {
    return {
      ...existingDocument?.[type?.matchToKey]?.acceptances?.at(-1),
      title: keyValObj[type?.matchToKey],
      originalDocUrl: originalDocMap[type?.matchToKey],
      matchToKey: typeMap[type?.matchToKey],
      // ⚠️ `type` (ex.: 'mediaRelease') é o que o `submitHandler` usa pra montar
      // `${type}Consent` / `${type}Url`. Sem ele o payload sai como
      // `undefinedConsent` e o backend não casa documento nenhum. O caminho do
      // gate (getContractsForState) já tinha, porque espalha o template inteiro;
      // este aqui não, e por isso aceitar pela tela do Profile não funcionava.
      type: type?.type
    };
  });

  const newresult = result
    ?.filter((r) => !!r)
    ?.map((p) => {
      const isExpired = dayjs(p?.expiresAt)?.isBefore(dayjs());
      return {
        ...p,
        isAccepted: p?.consent,
        url: p?.docUrl || p?.originalDocUrl,
        isExpired
      };
    });

  return newresult?.length > 0 ? newresult : null;
}

/**
 * allow generate sow and po or not
 */
export function isDisablePOAndSOW({ row }) {
  return (
    AGREEMENT_CONTRACTOR_TYPES.includes(row?.contractorType) &&
    !row?.isAgreementAccepted
  );
}

/**
 * Validates file(s) against allowed MIME types and extensions
 * Supports:
 *  - single File
 *  - File[]
 *  - FileList (e.target.files)
 *  - MIME types (e.g., "image/*", "application/pdf")
 *  - Extensions (e.g., ".pdf", ".jpg")
 *
 * @param {Object} params
 * @param {File|File[]|FileList} params.file - File input (single, array, or FileList)
 * @param {string[]} [params.allowedTypes=[]] - Allowed MIME types or extensions
 * @param {Function} params.dispatch - Redux dispatch function for alert dialog
 *
 * @returns {boolean} - true if all files are valid, false otherwise
 */
export const validateFileType = ({ file, allowedTypes = [], dispatch }) => {
  // normalize to array
  const files =
    file instanceof FileList
      ? Array.from(file)
      : Array.isArray(file)
        ? file
        : [file];

  if (!files.length || !files[0]) {
    dispatch(
      alertDialogAciton({
        open: true,
        title: "Error",
        message: "No file selected."
      })
    );
    return false;
  }

  // format types for UI
  const formatTypes = (types) =>
    types.map((type) => {
      if (type.endsWith("/*")) {
        return `${type.split("/")[0]} files`;
      }

      if (type.startsWith(".")) {
        return type.replace(".", "").toUpperCase(); // .pdf → PDF
      }

      return type.split("/")[1]?.toUpperCase() || type;
    });

  const isValid = allowedTypes.length
    ? files.every((f) => {
        // const ext = f.name.split(".").pop().toLowerCase();
        const nameParts = f?.name?.split(".") || [];
        const ext = nameParts.length > 1 ? nameParts.pop().toLowerCase() : "";

        return allowedTypes.some((type) => {
          // wildcard mime (image/*)
          if (type.endsWith("/*")) {
            return f.type?.startsWith(type.replace("/*", ""));
          }

          // extension (.pdf, .jpg)
          if (type.startsWith(".")) {
            return ext === type.replace(".", "").toLowerCase();
          }

          // exact mime
          return f.type === type;
        });
      })
    : true;

  if (!isValid) {
    dispatch(
      alertDialogAciton({
        open: true,
        title: "Error",
        message: `Invalid file type. Allowed file types are: ${
          formatTypes(allowedTypes).join(", ") || "any"
        }`
      })
    );
    return false;
  }

  return true;
};